import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Ban, Shield } from 'lucide-react';
import Button from '../../ui/Button';
import CommonModal from '../../ui/CommonModal';
import Input from '../../ui/Input';
import { Avatar } from '../../ui/Avatar';
import { User } from '../../../types';
import { cn } from '../../../utils';
import { useBanUser, useUnbanUser } from '../../../hooks/useApi';
import { toast } from 'react-hot-toast';
import { useAuth, usePermission } from '../../../hooks/useAuth';
import { useFormatDate } from '../../../hooks/useFormatDate';
import {
  BAN_PERIOD_OPTIONS,
  type BanPeriod,
  isAdminBanActive,
  isEffectivelyBanned,
  isShopBanActive,
  previewUnbanFromPeriod,
} from '../../../utils/banUtils';

interface BanUserModalProps {
  user: User | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

function playerPrimaryLine(u: User) {
  return u.phone?.trim() || u.email?.trim() || '—';
}

function playerSecondaryLine(u: User): string | null {
  const phone = u.phone?.trim();
  const email = u.email?.trim();
  if (phone && email) return email;
  return null;
}

const BanUserModal: React.FC<BanUserModalProps> = ({ user, isOpen, onClose, onSuccess }) => {
  const [reason, setReason] = useState('');
  const [banPeriod, setBanPeriod] = useState<BanPeriod | ''>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user: currentAdmin } = useAuth();
  // KIOSK-720: support_admin is view-only for Manage Cashiers/Players — cannot ban/unban.
  const canManageUsers = usePermission('userManagement') && currentAdmin?.role !== 'support_admin';
  const fmt = useFormatDate();

  const banUserMutation = useBanUser();
  const unbanUserMutation = useUnbanUser();

  const periodPreview = useMemo(() => previewUnbanFromPeriod(banPeriod, fmt), [banPeriod, fmt]);

  if (!isOpen || !user || !canManageUsers) return null;

  const adminBanActive = isAdminBanActive(user);
  const shopBanActive = isShopBanActive(user);
  const effectiveBanned = isEffectivelyBanned(user);

  const secondaryLine = playerSecondaryLine(user);
  const avatarUser = {
    id: user.id,
    name: user.name ?? undefined,
    userName: user.userName ?? undefined,
    username: user.username ?? undefined,
    email: user.email ?? undefined,
    profile: user.profile,
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (adminBanActive) {
      try {
        setIsSubmitting(true);
        await unbanUserMutation.mutateAsync({ userId: user.id });
        onSuccess();
        onClose();
        setReason('');
        setBanPeriod('');
      } catch (error) {
        console.error('Unban user error:', error);
      } finally {
        setIsSubmitting(false);
      }
    } else {
      if (!reason.trim() || !banPeriod) {
        return;
      }

      try {
        setIsSubmitting(true);
        await banUserMutation.mutateAsync({
          userId: user.id,
          data: { reason: reason.trim(), ban_period: banPeriod },
        });
        onSuccess();
        onClose();
        setReason('');
        setBanPeriod('');
      } catch (error) {
        toast.error('An error occurred while applying platform ban');
        console.error('Ban user error:', error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setReason('');
      setBanPeriod('');
      onClose();
    }
  };

  const canSubmitBan = reason.trim().length > 0 && banPeriod !== '';

  return (
    <CommonModal
      isOpen={isOpen}
      onClose={handleClose}
      size="md"
      closeDisabled={isSubmitting}
      title={
        adminBanActive ? (
          <>
            <Shield className="h-5 w-5 shrink-0 text-green-600" aria-hidden />
            Remove platform ban
          </>
        ) : (
          <>
            <Ban className="h-5 w-5 shrink-0 text-red-600" aria-hidden />
            Platform ban
          </>
        )
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="flex gap-3 rounded-lg border border-border/60 bg-muted/30 p-3">
          <Avatar user={avatarUser} size="md" deterministicColor className="shrink-0" />
          <div className="min-w-0 flex-1 space-y-1">
            <p className="truncate font-semibold leading-tight text-foreground">
              {playerPrimaryLine(user)}
            </p>
            {secondaryLine && (
              <p className="truncate text-sm text-muted-foreground">{secondaryLine}</p>
            )}
            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
              <span
                className={cn(
                  'inline-flex rounded-md px-1.5 py-0.5 font-medium',
                  effectiveBanned
                    ? 'bg-red-500/10 text-red-800 ring-1 ring-red-500/15'
                    : 'bg-emerald-500/10 text-emerald-800 ring-1 ring-emerald-500/15'
                )}
              >
                {effectiveBanned ? 'Restricted' : 'Active'}
              </span>
              {user.shop?.agent?.name?.trim() && (
                <span className="max-w-full truncate">
                  Agent:{' '}
                  {user.shop.agent?.id ? (
                    <Link
                      to={`/agents/${user.shop.agent.id}`}
                      className="font-medium text-primary underline-offset-2 hover:underline"
                    >
                      {user.shop.agent.name.trim()}
                    </Link>
                  ) : (
                    <span className="text-foreground">{user.shop.agent.name.trim()}</span>
                  )}
                </span>
              )}
              {user.shop?.name?.trim() && (
                <span className="max-w-full truncate">
                  Shop:{' '}
                  {user.shop?.id && user.shop.agent?.id ? (
                    <Link
                      to={`/shops/${user.shop.id}`}
                      className="font-medium text-primary underline-offset-2 hover:underline"
                    >
                      {user.shop.name.trim()}
                    </Link>
                  ) : (
                    <span className="text-foreground">{user.shop.name.trim()}</span>
                  )}
                </span>
              )}
            </div>
            <p className="font-mono text-[11px] text-muted-foreground/90" title={user.id}>
              ID: {user.id}
            </p>
          </div>
        </div>

        <div
          className={`flex items-start space-x-2 p-3 rounded-lg ${
            adminBanActive ? 'bg-green-50' : 'bg-red-50'
          }`}
        >
          <AlertTriangle
            className={`h-5 w-5 mt-0.5 ${adminBanActive ? 'text-green-600' : 'text-red-600'}`}
          />
          <div className="text-sm">
            <p
              className={`font-medium ${adminBanActive ? 'text-green-800' : 'text-red-800'}`}
            >
              {adminBanActive
                ? 'Remove platform restriction for this player?'
                : 'Apply a platform restriction to this player?'}
            </p>
            <p
              className={`text-xs mt-1 ${adminBanActive ? 'text-green-700' : 'text-red-700'}`}
            >
              {adminBanActive
                ? shopBanActive
                  ? 'Platform restriction will be removed. Player remains blocked while shop restriction is active.'
                  : 'Player can use the kiosk again if no other restriction is active.'
                : shopBanActive
                  ? 'This adds a platform restriction. Player remains blocked until all active restrictions are cleared.'
                  : 'Player will not be able to sign in or use the kiosk while this restriction is active.'}
            </p>
            {!adminBanActive && banPeriod === 'permanent' ? (
              <p className="text-xs mt-1 text-red-700">
                This restriction will not end automatically.
              </p>
            ) : null}
          </div>
        </div>

        {!adminBanActive && (
          <>
            <div className="space-y-2">
              <label htmlFor="ban-period" className="text-sm font-medium">
                Ban period <span className="text-red-500">*</span>
              </label>
              <select
                id="ban-period"
                value={banPeriod}
                onChange={(e) => setBanPeriod((e.target.value || '') as BanPeriod | '')}
                disabled={isSubmitting}
                required
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-50"
              >
                <option value="">Select ban period…</option>
                {BAN_PERIOD_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              {periodPreview ? (
                <p className="text-xs text-muted-foreground">{periodPreview}</p>
              ) : null}
            </div>
            <div className="space-y-2">
              <label htmlFor="ban-reason" className="text-sm font-medium">
                Ban reason <span className="text-red-500">*</span>
              </label>
              <Input
                id="ban-reason"
                placeholder="Enter reason for platform ban…"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                disabled={isSubmitting}
                required
              />
            </div>
          </>
        )}

        <div className="flex justify-end space-x-2 pt-4">
          <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant={adminBanActive ? 'primary' : 'destructive'}
            disabled={isSubmitting || (!adminBanActive && !canSubmitBan)}
          >
            {isSubmitting ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                {adminBanActive ? 'Removing…' : 'Applying…'}
              </div>
            ) : adminBanActive ? (
              'Remove platform ban'
            ) : (
              'Apply platform ban'
            )}
          </Button>
        </div>
      </form>
    </CommonModal>
  );
};

export default BanUserModal;
